import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ApiError, type Me, type Membership } from "../api.ts";
import { usedLabel } from "../access-rows.ts";
import { agentConnectionPrompt } from "@/lib/agent-connection-prompt";
import { exactTime } from "../relative-time.ts";
import { CheckIcon, CopyIcon } from "./icons.tsx";
import { useScopedApi } from "../use-scoped-api.ts";
import { usePendingActions } from "../pending-actions.ts";
import {
  QuietHistory,
  QuietLine,
  QuietLoading,
  QuietRow,
  QuietRows,
  QuietSection,
} from "./quiet.tsx";
import { Button } from "./ui/button.tsx";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./ui/alert-dialog.tsx";

const scopeLabels = {
  "appnotes:read": "Read feedback",
  "appnotes:write": "Write feedback",
  "appnotes:sites:create": "Register sites",
} as const;
const pageSchema = z.object({
  can_reset_sign_in: z.boolean().default(false),
  connections: z.array(
    z.object({
      id: z.string(),
      generation: z.number().int().positive(),
      client_id: z.string(),
      client_name: z.string(),
      organization_id: z.string(),
      organization_name: z.string().nullable(),
      scopes: z.array(
        z.enum(["appnotes:read", "appnotes:write", "appnotes:sites:create"]),
      ),
      created_at: z.iso.datetime(),
      last_used_at: z.iso.datetime().nullable(),
      revoked_at: z.iso.datetime().nullable(),
    }),
  ),
  next_after: z.string().nullable(),
});
const resetSchema = z.object({
  status: z.enum(["complete", "retry", "pending", "changed"]),
  operation_id: z.string().nullable(),
});
type Connection = z.infer<typeof pageSchema>["connections"][number];
type Inventory =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "ready"; readonly page: z.infer<typeof pageSchema> };
const buttonClass =
  "transition-transform active:scale-[0.96] motion-reduce:active:scale-100";

/** Personal browser approvals remain reachable even when the person has no team. */
export function ConnectedAgents({
  me,
  membership,
  onOpenAgentInstructions,
}: {
  readonly me: Me;
  readonly membership?: Membership;
  /** Opens the agent instructions dialog; Access passes it, the team gate does not. */
  readonly onOpenAgentInstructions?: () => void;
}) {
  const api = useScopedApi(me.user.id);
  const [inventory, setInventory] = useState<Inventory>({ kind: "loading" });
  const [pages, setPages] = useState<readonly (string | null)[]>([null]);
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState("");
  const inventoryRead = useRef(0);
  const after = pages.at(-1) ?? null;
  const endpoint = `/v1/me/mcp-connections${after === null ? "" : `?after=${encodeURIComponent(after)}`}`;
  async function readPage(signal?: AbortSignal) {
    const response = await api<unknown>(
      endpoint,
      signal === undefined ? {} : { signal },
    );
    const parsed = pageSchema.safeParse(response);
    if (!parsed.success)
      throw new ApiError(
        "The connection list could not be read. Please try again.",
        0,
        "invalid_response",
      );
    return parsed.data;
  }
  useEffect(() => {
    const controller = new AbortController();
    const read = ++inventoryRead.current;
    setNotice("");
    setInventory({ kind: "loading" });
    void readPage(controller.signal)
      .then((page) => {
        if (!controller.signal.aborted && read === inventoryRead.current)
          setInventory({ kind: "ready", page });
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted && read === inventoryRead.current)
          setInventory({ kind: "error", message: message(cause) });
      });
    return () => controller.abort();
  }, [api, endpoint, revision]);

  async function revoke(connection: Connection): Promise<void> {
    const read = ++inventoryRead.current;
    setNotice("");
    await api<void>(
      `/v1/me/mcp-connections/${encodeURIComponent(connection.id)}`,
      {
        method: "DELETE",
        csrf: me.csrf_token,
        body: { generation: connection.generation },
      },
    );
    // Confirm current state after a lost response or concurrent reconnection.
    // Never optimistically mark a stale generation as revoked.
    const page = await readPage();
    if (read === inventoryRead.current) setInventory({ kind: "ready", page });
    const current = page.connections.find((item) => item.id === connection.id);
    if (current !== undefined && current.revoked_at === null)
      throw new ApiError(
        "This connection changed. Close this dialog and review it before revoking again.",
        409,
        "connection_changed",
      );
    setNotice(
      `${connection.client_name || connection.client_id} access revoked. Existing notes are unchanged.`,
    );
  }
  async function reset(connection: Connection): Promise<void> {
    const read = ++inventoryRead.current;
    setNotice("");
    const response = resetSchema.safeParse(
      await api<unknown>(
        `/v1/me/mcp-connections/${encodeURIComponent(connection.id)}/reset-sign-in`,
        {
          method: "POST",
          csrf: me.csrf_token,
          body: { generation: connection.generation },
        },
      ),
    );
    if (!response.success)
      throw new ApiError(
        "The reset response could not be read. Check its status again.",
        0,
        "invalid_response",
      );
    const page = await readPage();
    if (read === inventoryRead.current) setInventory({ kind: "ready", page });
    const result = response.data;
    if (result.status === "changed")
      throw new ApiError(
        "This connection changed. Close this dialog and review its current state.",
        409,
        "connection_changed",
      );
    if (result.status === "retry")
      throw new ApiError(
        "Access is blocked. The sign-in provider could not be reached. Try the reset again.",
        503,
        "reset_retry",
      );
    if (result.status === "pending")
      throw new ApiError(
        `Access is blocked. The provider reset is not confirmed. Check its status again; if this persists, contact support with reference ${result.operation_id ?? "unavailable"}.`,
        503,
        "reset_pending",
      );
    setNotice(
      "Client sign-in reset. Sign out of this MCP connection in your coding client, then connect again and approve access in AppNotes. Existing notes are unchanged.",
    );
  }
  const mcpUrl = `${window.location.origin}/mcp`;
  const connections =
    inventory.kind === "ready" ? inventory.page.connections : [];
  const current = connections.filter((c) => c.revoked_at === null);
  const disconnected = connections.filter((c) => c.revoked_at !== null);
  const canReset =
    inventory.kind === "ready" && inventory.page.can_reset_sign_in;
  const row = (connection: Connection) => (
    <ConnectionRow
      key={connection.id}
      connection={connection}
      currentOrganizationId={membership?.organization_id ?? null}
      revoke={revoke}
      reset={canReset ? reset : undefined}
    />
  );
  return (
    <QuietSection
      action={
        <>
          {onOpenAgentInstructions === undefined ? null : (
            <button
              className="text-action-strong"
              onClick={onOpenAgentInstructions}
              type="button"
            >
              Set up an agent
            </button>
          )}
          <QuietCopyAction label="Copy MCP URL" value={mcpUrl} />
        </>
      }
      description="Agents you approved through MCP."
      title="Connected agents"
      titleId="connected-agents-title"
    >
      <p className="quiet-note empty:hidden" role="status">
        {notice}
      </p>
      {inventory.kind === "loading" ? (
        <QuietLoading label="Loading connected agents" />
      ) : inventory.kind === "error" ? (
        <QuietLine
          action={
            <button
              className="text-action"
              onClick={() => setRevision((n) => n + 1)}
              type="button"
            >
              Retry
            </button>
          }
          tone="danger"
        >
          {inventory.message}
        </QuietLine>
      ) : current.length === 0 ? (
        <QuietLine
          action={
            membership === undefined ? undefined : (
              <QuietCopyAction
                label="Copy agent prompt"
                value={agentConnectionPrompt({
                  apiOrigin: window.location.origin,
                  membership,
                  mcpAvailable: true,
                })}
              />
            )
          }
        >
          No connected agents yet. Add the MCP URL to your coding agent and sign
          in; AppNotes asks you to approve the team and access.
        </QuietLine>
      ) : (
        <QuietRows label="Connected agents">{current.map(row)}</QuietRows>
      )}
      {disconnected.length === 0 ? null : (
        <QuietHistory summary={`${disconnected.length} disconnected`}>
          <QuietRows label="Disconnected agents">
            {disconnected.map(row)}
          </QuietRows>
        </QuietHistory>
      )}
      {(pages.length > 1 ||
        (inventory.kind === "ready" && inventory.page.next_after !== null)) && (
        <p className="quiet-pager">
          <button
            className="text-action"
            disabled={pages.length === 1 || inventory.kind === "loading"}
            onClick={() => setPages((list) => list.slice(0, -1))}
            type="button"
          >
            Previous
          </button>
          <span className="tabular-nums">Page {pages.length}</span>
          <button
            className="text-action"
            disabled={
              inventory.kind !== "ready" || inventory.page.next_after === null
            }
            onClick={() => {
              if (
                inventory.kind === "ready" &&
                inventory.page.next_after !== null
              )
                setPages((list) => [...list, inventory.page.next_after]);
            }}
            type="button"
          >
            Next
          </button>
        </p>
      )}
    </QuietSection>
  );
}

/** A quiet text action with the copy glyph that says "Copied" for 1.5 s. */
function QuietCopyAction({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (state === "idle") return;
    const timeout = window.setTimeout(() => setState("idle"), 1_500);
    return () => window.clearTimeout(timeout);
  }, [state]);
  return (
    <button
      className="text-action-strong"
      onClick={() => {
        navigator.clipboard.writeText(value).then(
          () => setState("copied"),
          () => setState("failed"),
        );
      }}
      title={value.length < 120 ? value : undefined}
      type="button"
    >
      {state === "copied" ? <CheckIcon /> : <CopyIcon />}
      <span aria-live="polite">
        {state === "copied"
          ? "Copied"
          : state === "failed"
            ? "Copy failed"
            : label}
      </span>
    </button>
  );
}

function ConnectionRow({
  connection: c,
  currentOrganizationId,
  revoke,
  reset,
}: {
  readonly connection: Connection;
  readonly currentOrganizationId: string | null;
  readonly revoke: (connection: Connection) => Promise<void>;
  readonly reset: ((connection: Connection) => Promise<void>) | undefined;
}) {
  // Keep the approved target stable across refreshes and ambiguous retries.
  // A new grant generation requires closing and reopening confirmation.
  const [selection, setSelection] = useState<Connection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = usePendingActions();
  const title = useRef<HTMLHeadingElement>(null);
  const busy = pending.isPending("revoke");
  const resetting = (selection ?? c).revoked_at !== null;
  async function confirm(): Promise<void> {
    if (selection === null) return;
    await pending.run("revoke", async () => {
      setError(null);
      try {
        if (selection.revoked_at !== null && reset !== undefined)
          await reset(selection);
        else await revoke(selection);
        setSelection(null);
      } catch (cause) {
        setError(message(cause));
      }
    });
  }
  const name = c.client_name || "Coding client";
  const team =
    c.organization_name === null
      ? "No team access"
      : c.organization_id === currentOrganizationId
        ? null
        : c.organization_name;
  return (
    <QuietRow
      actions={
        <AlertDialog
          open={selection !== null}
          onOpenChange={(next) => {
            if (!busy) {
              setSelection(next ? c : null);
              setError(null);
            }
          }}
        >
          {(c.revoked_at === null || reset !== undefined) && (
            <AlertDialogTrigger
              render={<button className="text-action" type="button" />}
            >
              {c.revoked_at === null ? "Disconnect" : "Reset client sign-in"}
            </AlertDialogTrigger>
          )}
          <AlertDialogContent finalFocus={title}>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {resetting
                  ? "Reset client sign-in?"
                  : `Disconnect ${(selection ?? c).client_name || "this agent"}?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {resetting
                  ? "This clears your WorkOS authorization for this coding client, including its access across AppNotes teams and any other resources authorized through the same provider application. Other people’s connections and existing notes are unchanged. After the reset, connect again in your coding client and approve the access you want."
                  : `This stops this client’s access to ${(selection ?? c).organization_name ?? "its former team"} through your approval. It does not delete notes or affect anyone else’s connections. To reconnect later, reset the client sign-in here first.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {error !== null && (
              <p className="text-[13px] text-danger" role="alert">
                {error}
              </p>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
              <Button
                className={buttonClass}
                variant="danger"
                aria-disabled={busy}
                aria-busy={busy}
                onClick={() => void confirm()}
              >
                {busy
                  ? resetting
                    ? "Checking reset…"
                    : "Disconnecting…"
                  : resetting
                    ? error === null
                      ? "Reset client sign-in"
                      : "Check reset status"
                    : "Disconnect"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      }
      detail={
        c.revoked_at !== null ? (
          "Disconnected"
        ) : c.last_used_at === null ? (
          "Not used yet"
        ) : (
          <time dateTime={c.last_used_at} title={exactTime(c.last_used_at)}>
            {usedLabel(c.last_used_at)}
          </time>
        )
      }
      dimmed={c.revoked_at !== null}
      name={
        <>
          <h3 ref={title} tabIndex={-1} title={c.client_id}>
            {name}
          </h3>
          {team === null ? null : <small>{team}</small>}
        </>
      }
      title={`${c.scopes.map((scope) => scopeLabels[scope]).join(" · ")}. First connected ${exactTime(c.created_at)}.`}
    />
  );
}

/** Two-letter mono monogram for an agent client: "Claude Code" reads "Cl". */
function message(cause: unknown): string {
  return cause instanceof ApiError
    ? cause.message
    : "AppNotes could not update your connections. Please try again.";
}
